import { MouseEventHandler, useCallback, useMemo, useRef } from "react";
import useClickOutside from "domains/assets/hooks/useClickOutside";
import { useHotkeys } from "domains/commons/contexts/HotkeysProvider";
import { Tools } from "domains/home/components/SectionTools";
import { TOP_BAR_HEIGHT } from "domains/navigation/constants/TopBar";
import useRouter from "domains/navigation/hooks/useRouter";
import { appShortcuts } from "domains/shortcuts/components/appShortcuts";
import { extraTheme } from "domains/theme";
import Button, { ButtonProps } from "domains/ui/components/Button";
import Icon from "domains/ui/components/Icon";
import { useItemTabIndicator } from "domains/ui/hooks/useItemTabIndicator";
import { useUserContext } from "domains/user/contexts/UserProvider";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";

import { Box, HStack, useDisclosure, VStack } from "@chakra-ui/react";

/**
 * This is the top navigation bar for desktop and larger screens
 */
export default function NavBar({ isCompact = false }: { isCompact?: boolean }) {
  const router = useRouter();
  const { isUnsigned, openSignUpModal } = useUserContext();
  const {
    isOpen: isToolsOpen,
    onToggle: onToggleTools,
    onClose: onCloseTools,
  } = useDisclosure();
  const navbarRef = useRef<HTMLDivElement>(null);
  const tabIndicatorRef = useRef<HTMLDivElement>(null);

  const activeItem = useMemo(() => {
    if (isToolsOpen) return "create";
    if (router.pathname === "/") return "home";
    if (router.pathname === "/assets") return "assets";
    if (router.pathname.startsWith("/explore")) return "explore";
  }, [router.pathname, isToolsOpen]);

  // ----------------------------------

  const handleExploreClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      if (isUnsigned) {
        e.preventDefault();
        openSignUpModal("generate");
      }
      onCloseTools();
    },
    [onCloseTools, isUnsigned, openSignUpModal]
  );

  // ----------------------------------

  useHotkeys(appShortcuts.assetPage.shortcuts.close.shortcut, onCloseTools, [
    onCloseTools,
  ]);

  useClickOutside(onCloseTools, !isToolsOpen);

  // ----------------------------------

  useItemTabIndicator({
    containerRef: navbarRef,
    tabIndicatorRef: tabIndicatorRef,
    activeItemDataId: activeItem ? `navbar-item-${activeItem}` : undefined,
  });

  return (
    <>
      <HStack
        ref={navbarRef}
        pos="relative"
        justify="center"
        overflow="hidden"
        w="full"
        h="full"
        spacing={isCompact ? 2 : 4}
      >
        <Box
          ref={tabIndicatorRef}
          pos="absolute"
          borderRadius="md"
          transition={extraTheme.transitions.fast}
          bgColor="textPrimary"
        />

        <NavBarItem
          id="home"
          isActive={activeItem === "home"}
          internalLink="/"
          onClick={() => {
            Track(AnalyticsEvents.Navigation.ClickedHome, {});
            onCloseTools();
          }}
          data-testid="navbar-home-button"
        >
          Home
        </NavBarItem>

        <NavBarItem
          id="create"
          isActive={activeItem === "create"}
          rightIcon={
            <Icon id={isToolsOpen ? "Ui/ChevronUpSm" : "Ui/ChevronDownSm"} />
          }
          onClick={onToggleTools}
          data-outside-click-excluded="true"
          data-testid="navbar-create-button"
        >
          Create
        </NavBarItem>

        {!isUnsigned && (
          <NavBarItem
            id="assets"
            isActive={activeItem === "assets"}
            internalLink="/assets"
            onClick={() => {
              Track(AnalyticsEvents.Navigation.ClickedAssets, {});
              onCloseTools();
            }}
            data-testid="navbar-assets-button"
          >
            Assets
          </NavBarItem>
        )}

        <NavBarItem
          id="explore"
          isActive={activeItem === "explore"}
          internalLink="/explore"
          onClick={(e) => {
            Track(AnalyticsEvents.Navigation.ClickedExplore, {});
            handleExploreClick(e);
          }}
          data-testid="navbar-explore-button"
        >
          Explore
        </NavBarItem>

        <NavBarItem
          id="learn"
          externalLink="https://help.scenario.com"
          onClick={() => {
            Track(AnalyticsEvents.Navigation.ClickedHelpCenter, {});
            onCloseTools();
          }}
          data-testid="navbar-learn-button"
        >
          Learn
        </NavBarItem>
      </HStack>

      <VStack
        pos="absolute"
        top="100%"
        left={0}
        align="stretch"
        display={!isToolsOpen ? "none" : undefined}
        w="100%"
        h={`calc(100vh - ${TOP_BAR_HEIGHT}px)`}
        minH="200px"
        mt="1px"
        backdropFilter="blur(30px)"
        bgColor="rgba(0, 0, 0, 0.1)"
        spacing={0}
      >
        <Box flex={0} maxW="1400px" mx="auto" p={8}>
          <Tools isBlurred context="topbar" onButtonClick={onCloseTools} />
        </Box>
      </VStack>
    </>
  );
}

// ------------------------------------

type NavBarItemProps = ButtonProps & {
  id: string;
  isActive?: boolean;
};

function NavBarItem({ id, isActive = false, ...props }: NavBarItemProps) {
  const dataId = `navbar-item-${id}`;
  return (
    <Button
      size="sm"
      variant={isActive ? "primary" : "filterGhost"}
      colorScheme={isActive ? "white" : undefined}
      bgColor={isActive ? "transparent !important" : undefined}
      px={2}
      gap={1}
      data-id={dataId}
      {...props}
    />
  );
}
